import { Dimensions } from 'editor/src/store/design/types';
import { PluginName } from 'editor/src/store/plugins/types';

import { ShutterstockImageItem } from 'editor/src/component/DesktopSidebar/TabContents/ShutterstockTabContent/types';

export interface GalleryState {
  images: GalleryImage[];
  hideUsedPhotos: boolean;
  sessionImages: string[];
  selectedImagesIds: string[];
  // uploader ids of this session's upload batches, newest batch first, each in file-name order;
  // a split PDF's id is replaced by its pages' image ids
  uploadBatches?: string[][];
  // PDFs the host is splitting into one image per page, as last reported by the host
  pdfSplits?: PdfSplitProgress[];
}

export type PdfSplitStatus = 'splitting' | 'saving' | 'done' | 'failed';

// sent by the host as gallery.pdfSplitProgress while an uploaded PDF becomes one image per page
export interface PdfSplitProgressMessage {
  uploaderId: string;
  fileName: string;
  status: PdfSplitStatus;
  pagesTotal?: number;
  pagesSaved?: number;
  // image ids by PDF page, null where a page could not be saved, sent once every page is handled
  pageImageIds?: (string | null)[];
}

export interface PdfSplitProgress extends PdfSplitProgressMessage {
  // set by the editor once the user closes the notice
  dismissed?: boolean;
  // PDF pages Fill could not place because their image was not in the gallery
  unplacedPdfPages?: number[];
}

export enum MimeType {
  'SVG' = 'SVG',
  'PDF' = 'PDF',
  'JPG' = 'JPG',
  'PNG' = 'PNG',
  'WebP' = 'WebP',
  'Unknown' = 'Unknown',
}
export interface GalleryImage {
  id: string;
  uploaderId?: string;
  uuid: number;
  name: string;
  type: MimeType;
  width: number;
  height: number;
  hasAssetDimensions: boolean;
  state: ImageState;
  url?: string;
  thumbnailUrl?: string;
  quantizedUrl?: string;
  previewData?: string;
  label?: string;
  bytesTotal?: number;
  bytesUploaded?: number;
  source?: string;
  colors?: string[];
  quantizedColors?: string[];
  hidden?: true;
  from?: { type: PluginName.Graphics; addonId: string };
  hideProgressPercentage?: boolean;
  exif?: ExifData;
  digitizationStatus?: AssetDigitizationStatus;
  // physical size (mm) the asset was digitized at, used to restore the digitized placement
  digitizedSizeMm?: Dimensions;
  // printed size of a PDF page; the preview image it is shown with is far smaller
  pageSizeMm?: Dimensions;
  hasTransparentBackground?: boolean;
  printDetail?: ImagePrintDetail;
  /** Set by the host for images the signed-in customer owns; only those can go to their library. */
  savableToLibrary?: boolean;
}

// What the print checks need to know about an image, measured once per image URL on a downscaled copy.
export interface ImagePrintDetail {
  // the URL measured: an upload swaps the preview URL for the stored one under the same image id
  url: string;
  // share of visible pixels that are only partly opaque
  semiTransparentRatio: number;
  // share of opaque pixels in smooth colour transitions
  gradientRatio: number;
  // width of the raster the analysis ran on, to turn a size in mm into raster pixels
  rasterWidth: number;
  // [k] = share of opaque pixels in parts narrower than k raster pixels; empty for an image without transparency
  thinRatioByWidth: number[];
}

export enum AssetDigitizationStatus {
  DIGITIZING = 'digitizing',
  DIGITIZED = 'digitized',
  FAILED = 'failed',
}

export interface LocalPreviewImageData extends BaseImage {
  preview: string | undefined;
  type: string;
  name: string;
}

export interface RemotePreviewImageData extends BaseImage {
  preview: string;
  type: string;
  name: string;
}

export interface UploadedImageData {
  file: BaseImage;
  url: string;
  thumbnailUrl: string | undefined;
  quantizedUrl?: string;
  mimeType: string;
  hidden?: true;
}

export interface BaseImage {
  id: string;
  name?: string;
  type?: string;
  width?: number;
  height?: number;
  label?: string;
  source?: string;
  colors?: ImageColors;
  quantizedColors?: string[];
  hideProgressPercentage?: boolean;
  exif?: ExifData;
  digitizationStatus?: AssetDigitizationStatus;
  // physical size (mm) the asset was digitized at, used to restore the digitized placement
  digitizedSizeMm?: Dimensions;
  pageSizeMm?: Dimensions;
  savableToLibrary?: boolean;
}

export interface ExifData {
  dateCreated?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
}

export interface FailedUploadImageData {
  file: BaseImage;
}

export interface ProgressImageData {
  file: BaseImage & { preview: string | undefined; isRemote: boolean };
  bytesTotal: number;
  bytesUploaded: number;
}

export interface ShutterstockImageData {
  assetProviderUid: string;
  image: ShutterstockImageItem;
}

export enum ImageState {
  UPLOADED = 'UPLOADED',
  LOCAL_DROP = 'LOCAL_DROP',
  LOCAL_PREVIEW = 'LOCAL_PREVIEW',
  REMOTE_PREVIEW = 'REMOTE_PREVIEW',
  FAILED = 'FAILED',
}

export interface ImageColors {
  fill?: string[];
  stroke?: string[];
}

export interface UploadedAsset {
  pluginAssetId: string;
  assetId: string;
  url: string;
  thumbnailUrl: string;
  mimeType: string;
  colors?: ImageColors;
  quantizedColors?: string[];
  width: number;
  height: number;
  label: string;
}

export interface AssetUpdatePayload {
  assetId: string;
  filename: string;
  folderId?: string;
}
